import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DollarSign, Users, TrendingUp, Copy, Check, ExternalLink } from "lucide-react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AffiliateDashboard() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!user.is_affiliate) {
      navigate("/account");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, commissionsRes] = await Promise.all([
        axios.get(`${API}/affiliate/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/affiliate/commissions`, { headers: getAuthHeaders() })
      ]);
      setStats(statsRes.data);
      setCommissions(commissionsRes.data);
    } catch (error) {
      console.error("Failed to fetch affiliate data:", error);
      toast.error("Failed to load affiliate data");
    } finally {
      setLoading(false);
    }
  };

  const referralLink = `${window.location.origin}?ref=${user?.affiliate_code}`;

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success("Link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (!user || !user.is_affiliate) return null;

  return (
    <div className="container-main section-spacing">
      <div className="mb-8">
        <h1 className="text-h2 mb-2">Affiliate Dashboard</h1>
        <p className="text-muted-foreground">
          Track your referrals and earnings. Earn 20% commission on every sale.
        </p>
      </div>

      {/* Referral Link */}
      <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 mb-8">
        <h2 className="font-heading text-lg mb-4">Your Referral Link</h2>
        <div className="flex gap-2">
          <Input
            value={referralLink}
            readOnly
            className="bg-white font-mono text-sm"
            data-testid="referral-link"
          />
          <Button 
            onClick={copyLink}
            className="btn-primary min-w-[100px]"
            data-testid="copy-link-btn"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </>
            )}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          Share this link and earn 20% on every purchase made through it.
        </p>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse card-dashboard">
              <div className="h-4 bg-muted rounded w-1/2 mb-4" />
              <div className="h-8 bg-muted rounded w-1/3" />
            </div>
          ))}
        </div>
      ) : stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card-dashboard" data-testid="total-referrals">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <span className="text-muted-foreground">Total Referrals</span>
            </div>
            <p className="text-3xl font-heading">{stats.total_referrals}</p>
          </div>

          <div className="card-dashboard" data-testid="total-earnings">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-muted-foreground">Total Earnings</span>
            </div>
            <p className="text-3xl font-heading text-green-600">${stats.total_earnings.toFixed(2)}</p>
          </div>

          <div className="card-dashboard" data-testid="pending-earnings">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-yellow-600" />
              </div>
              <span className="text-muted-foreground">Pending Payout</span>
            </div>
            <p className="text-3xl font-heading text-yellow-600">${stats.pending_earnings.toFixed(2)}</p>
          </div>
        </div>
      )}

      {/* Commission History */}
      <div className="card-dashboard">
        <h2 className="font-heading text-xl mb-6">Commission History</h2>
        
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse p-4 bg-muted/30 rounded-lg">
                <div className="h-4 bg-muted rounded w-1/4 mb-2" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : commissions.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">No commissions yet</p>
            <p className="text-sm text-muted-foreground">
              Share your referral link to start earning!
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium">Date</th>
                  <th className="text-left py-3 px-4 font-medium">Order ID</th>
                  <th className="text-left py-3 px-4 font-medium">Commission</th>
                  <th className="text-left py-3 px-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((commission) => (
                  <tr key={commission.id} className="border-b last:border-0">
                    <td className="py-3 px-4">
                      {new Date(commission.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 font-mono text-sm">
                      #{commission.order_id.slice(0, 8)}
                    </td>
                    <td className="py-3 px-4 font-medium text-green-600">
                      ${commission.amount.toFixed(2)}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        commission.status === "paid" 
                          ? "bg-green-100 text-green-800" 
                          : "bg-yellow-100 text-yellow-800"
                      }`}>
                        {commission.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
